// @ts-nocheck
/**
 * ExpandPanel — inline expand/collapse panel.
 *
 * Renders as a zero-height div when closed, expanding to its natural content
 * height when open. This keeps it in the normal document flow so parent
 * ResizeObserver callbacks fire automatically.
 *
 * Replaces FlyoutPanel (position: absolute) for TOC, Language, and hamburger
 * panels in WebViewNavBar.
 *
 * Props:
 *   open          — boolean; whether the panel is expanded
 *   onClose       — fn(); called on outside-click or Escape key
 *   children      — panel content
 *   style         — optional style overrides on the outer wrapper div
 *   align         — "left" | "right" | "center" (default "left")
 *                   When the panel is narrower than its content, which edge
 *                   to anchor it to. Implemented via CSS on the inner box.
 *   minWidth      — CSS string, default "240px"
 *   maxWidth      — CSS string, default "360px"
 *   className     — optional CSS class on outer div
 *   excludeRef    — optional React ref; outside-click handler will not fire
 *                   if the click target is within excludeRef.current. Use this
 *                   to prevent the panel from closing when the trigger button
 *                   is clicked (toggle behavior needs to be handled by the
 *                   trigger, not the panel's close handler).
 */

import React, { useRef, useEffect, useState } from "react";

const font = "'Open Sans', system-ui, sans-serif";

function borderRadius(align) {
  if (align === "right") return "8px 0 8px 8px";
  if (align === "center") return "8px";
  return "0 8px 8px 8px";
}

export default function ExpandPanel({
  open,
  onClose,
  children,
  style,
  align = "left",
  minWidth = "240px",
  maxWidth = "360px",
  className,
  excludeRef,
}) {
  const ref = useRef(null);

  const [reducedMotion] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (excludeRef?.current?.contains(e.target)) return;
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose, excludeRef]);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (e.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      ref.current?.querySelector("button, a, [tabindex]")?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [open]);

  const closedStyles = {
    visibility: "hidden",
    height: 0,
    overflow: "hidden",
    pointerEvents: "none",
    opacity: 0,
  };

  const openStyles = {
    visibility: "visible",
    height: "auto",
    overflow: "visible",
    pointerEvents: "auto",
    opacity: 1,
  };

  const stateStyles = open ? openStyles : closedStyles;

  const innerStyle = {
    fontFamily: font,
    background: "#fff",
    borderRadius: borderRadius(align),
    border: "1px solid rgba(122,128,145,0.18)",
    boxShadow: "4px 8px 20px rgba(0,0,0,0.14)",
    minWidth,
    maxWidth,
    overflowY: "auto",
    maxHeight: "60vh",
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(0,102,204,0.25) transparent",
    WebkitOverflowScrolling: "touch",
    transition: reducedMotion ? undefined : "opacity 0.15s ease",
    ...stateStyles,
  };

  const outerStyle = {
    display: "block",
    position: "relative",
    width: "100%",
    ...style,
  };

  return (
    <div style={outerStyle} className={className}>
      <div ref={ref} style={innerStyle}>
        {children}
      </div>
    </div>
  );
}
